import { ModelListItem } from 'src/api/models';
import { collapsePairs } from 'src/utils/modelPairs';

/**
 * Collapses each Quick/Full pair in the Settings models list to a single
 * card whenever both halves are Active, keeping the SAME model the navbar
 * dropdown shows so the two surfaces never disagree:
 *
 * - both Active, only the Quick has a published version -> keep the Quick
 *   (that is what the navbar lists);
 * - both Active otherwise (Full published, both published, or neither
 *   published) -> keep the Full;
 * - any other status combination (running / failed / pending on either
 *   half) -> keep both cards so the user can track each status separately.
 *
 * Pair identity is modelName + createdAt: models are created as a pair in
 * one INSERT (the backend strips the "(Quick)"/"(Full)" name suffix in the
 * list response), and the backend uses the same key to find the paired
 * model. Unpaired models are always shown.
 */
export const collapseActivePairs = (models: ModelListItem[]): ModelListItem[] =>
  collapsePairs(models, {
    isQuick: (model) => model.quickRun,
    pairKey: (model) => `${model.createdAt}::${model.modelName}`,
    resolve: (quick, full) => {
      if (quick.status !== 'success' || full.status !== 'success') {
        return 'both';
      }
      // hasPublishedVersion may be absent on old-backend responses (deploy
      // skew); treat unknown as "not published by the quick", i.e. keep Full.
      const quickPublished = quick.hasPublishedVersion === true;
      const fullPublished = full.hasPublishedVersion === true;
      return quickPublished && !fullPublished ? 'quick' : 'full';
    },
  });
